import { ArticleOptionsServices } from '../../global';
import { FT5_OFFERS_ARTICLE_OPTION_CHANGE } from '../../_common/client/offers-tracking';
import { BaseComponent2 } from '../../_common/client/base-component2';
import { unsetAll, withEvents } from '@otto-ec/global-resources/event';
import { OcSheetV1Events, sheetV1 } from '@otto-ec/otto-components/sheet';

export class OffersArticleOptionsComponent extends BaseComponent2 {
    public rootSelector = '.js_of_offers-article-options';

    private variationId?: string;
    private useAvailabilityDetailSlowFeature = false;
    private deliveryOnDemand = false;

    public async onReloadComponentEvent(): Promise<void> {
        this.variationId = this.getRootElement().dataset.variationId;
        this.deliveryOnDemand = this.getRootElement().dataset.variationDeliveryOnDemand === 'true';
        this.useAvailabilityDetailSlowFeature = this.getRootElement().dataset.useAvailabilityDetailSlowFeature === 'true';

        /*                                                                               */
        this.emitSelectedForOrdering();

        this.getCheckBoxes().forEach((chkBox: HTMLInputElement) => {
            /*                                                                       */
            this.updateWarningState(chkBox);

            const itemElement = chkBox.closest<HTMLElement>('.js_of_offers-article-options__item');
            const confirmationInSheetRequired = itemElement?.dataset.confirmationInSheetRequired === 'true';
            const encodedContentUrl = itemElement?.dataset.encodedContentUrl || '';

            if (confirmationInSheetRequired) {
                chkBox.addEventListener('click', e => {
                    /*                                                           */
                    /*                                                           */
                    if (chkBox.checked) {
                        void this.openConfirmationSheet(encodedContentUrl, chkBox);
                        /*                                                                               */
                        e.preventDefault();
                    }
                });
            }

            chkBox.addEventListener('change', () => this.onChkBoxChanged(chkBox));
        });
    }

    private async openConfirmationSheet(encodedContentUrl: string, chkBox: HTMLInputElement): Promise<void> {
        const sheet = await sheetV1.create({
            id: `of_offers-article-options__content-sheet-${chkBox.id}`,
            base64Url: encodedContentUrl,
        });

        const on = withEvents<OcSheetV1Events>(sheet);
        const unset = unsetAll(
            on('oc-sheet-content-parse', evt => {
                evt.detail.parsed.querySelector('[slot="actions"]')?.remove();
                evt.detail.parsed.appendChild(this.createActionSlotElement(chkBox, sheet));
                unset();
            }),
            on('oc-sheet-close', () => unset())
        );

        sheet.open = true;
    }

    private createActionSlotElement(chkBox: HTMLInputElement, sheetElement: HTMLOcSheetV1Element): HTMLDivElement {
        const actionSlotElement = document.createElement('div');
        actionSlotElement.slot = 'actions';

        const submitButtonElement = document.createElement('oc-button-v1');
        submitButtonElement.variant = 'primary';
        submitButtonElement.textContent = 'Hinzufügen';
        submitButtonElement.addEventListener('click', () => {
            chkBox.checked = true;
            this.onChkBoxChanged(chkBox);
            sheetElement.close();
        });

        const cancelButtonElement = document.createElement('oc-button-v1');
        cancelButtonElement.variant = 'secondary';
        cancelButtonElement.className = 'oc-mt-50';
        cancelButtonElement.textContent = 'Ohne fortfahren';
        cancelButtonElement.addEventListener('click', () => {
            chkBox.checked = false;
            this.onChkBoxChanged(chkBox);
            sheetElement.close();
        });

        actionSlotElement.append(submitButtonElement, cancelButtonElement);
        return actionSlotElement;
    }

    private onChkBoxChanged(chkBox: HTMLInputElement) {
        this.updateWarningState(chkBox);
        this.emitSelectedForTracking(chkBox);
        this.emitSelectedForOrdering();
    }

    /**
 *
 */
    private emitSelectedForTracking(chkBox: HTMLInputElement): void {
        o_global.eventQBus.emit(FT5_OFFERS_ARTICLE_OPTION_CHANGE, {
            variationId: this.variationId,
            changedServiceId: chkBox.dataset.articleOptionId,
            isSelected: chkBox.checked,
        });
    }

    /**
 *
 */
    private emitSelectedForOrdering(): void {
        o_global.eventQBus.emit(`ft5.offersArticleOptionChange.${this.variationId}`, {
            services: this.getSelectedServicesForOrdering().services,
        });
    }

    private getCheckBoxes(): HTMLInputElement[] {
        return this.getChildElements('.js_of_offers-article-options__checkbox-input') as HTMLInputElement[];
    }

    private updateWarningState(chkBox: HTMLInputElement): void {
        let showWarning: boolean;
        if (this.useAvailabilityDetailSlowFeature) {
            const notApplicableForDesiredDate = chkBox.dataset.deliveryForDesiredDate === 'false';
            showWarning = chkBox.checked && this.deliveryOnDemand && notApplicableForDesiredDate;
        } else {
            const isOptionDeliveryLonger24h = chkBox.dataset.deliveryForDesiredDate === 'false';
            showWarning = chkBox.checked && isOptionDeliveryLonger24h;
        }
        this.setWarningVisibility(chkBox, showWarning);
    }

    private setWarningVisibility(chkBox: HTMLInputElement, shouldShowWarning: boolean): void {
        const warningElem = chkBox.parentElement?.parentElement?.querySelector<HTMLDivElement>('.js_of_offers-article-options__warning');
        if (!warningElem) {
            throw new Error('cant find warning element for input' + chkBox.id);
        }

        if (shouldShowWarning) {
            warningElem.classList.remove('pl_hidden');
        } else {
            warningElem.classList.add('pl_hidden');
        }
    }

    private getSelectedServicesForOrdering(): ArticleOptionsServices {
        const services = this.getCheckBoxes()
            .filter(chkBox => chkBox.checked)
            .map(chkBox => ({ uuid: chkBox.dataset.articleOptionId ?? '' }));

        return { services };
    }
}
